(() => {
  if (customElements.get('lemurea-hero-carousel')) return;

  class LemureaHeroCarousel extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.abort) return;
        this.abort = new AbortController();
        this.track = this.querySelector('[data-hero-track]');
        this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
        if (!this.track || this.slides.length < 2) return;
        this.current = 0;
        this.userPaused = false;
        this.hovered = false;
        this.focusPaused = false;
        this.visible = false;
        this.autoplay = this.dataset.autoplay === 'true';
        this.interval = Math.max(4, Math.min(12, Number(this.dataset.interval) || 6)) * 1000;
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.controls = this.querySelector('[data-hero-controls]');
        this.counter = this.querySelector('[data-hero-counter]');
        this.status = this.querySelector('[data-hero-status]');
        this.toggle = this.querySelector('[data-hero-toggle]');
        const signal = this.abort.signal;
        this.controls.hidden = false;
        this.track.addEventListener('scroll', () => {
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => {
            const left = this.track.getBoundingClientRect().left;
            let nearest = 0;
            this.slides.forEach((slide, index) => {
              if (Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(this.slides[nearest].getBoundingClientRect().left - left)) nearest = index;
            });
            this.show(nearest, !this.autoMoving);
            this.autoMoving = false;
          }, 150);
        }, { passive: true, signal });
        this.addEventListener('click', (event) => {
          const button = event.target.closest('button');
          if (!button || !this.controls.contains(button)) return;
          if (button.hasAttribute('data-hero-toggle')) {
            this.userPaused = !this.userPaused;
            if (!this.userPaused) this.focusPaused = false;
            this.schedule();
            return;
          }
          this.userPaused = true;
          this.go(this.current + (button.hasAttribute('data-hero-next') ? 1 : -1));
        }, { signal });
        this.addEventListener('keydown', (event) => {
          if (event.target !== this && !this.controls.contains(event.target)) return;
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.userPaused = true;
          this.go(this.current + (event.key === 'ArrowRight' ? 1 : -1));
        }, { signal });
        this.track.addEventListener('pointerdown', () => {
          this.userPaused = true;
          this.schedule();
        }, { passive: true, signal });
        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.hovered = true;
          this.schedule();
        }, { signal });
        this.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          this.hovered = false;
          this.schedule();
        }, { signal });
        this.addEventListener('focusin', () => {
          this.focusPaused = true;
          this.schedule();
        }, { signal });
        this.addEventListener('focusout', (event) => {
          if (this.contains(event.relatedTarget)) return;
          this.focusPaused = false;
          this.schedule();
        }, { signal });
        document.addEventListener('visibilitychange', () => {
          this.schedule();
          this.syncVideos();
        }, { signal });
        this.motion.addEventListener('change', () => {
          this.schedule();
          this.syncVideos();
        }, { signal });
        document.addEventListener('shopify:block:select', (event) => {
          if (String(event.detail?.sectionId) !== this.dataset.sectionId) return;
          const index = this.slides.findIndex(slide => slide.dataset.blockId === String(event.detail?.blockId));
          if (index >= 0) this.go(index, false);
        }, { signal });
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          this.schedule();
          this.syncVideos();
        });
        this.visibilityObserver.observe(this);
        this.resizeObserver = new ResizeObserver(() => this.go(this.current, false));
        this.resizeObserver.observe(this.track);
        this.show(0, false);
        this.schedule();
      });
    }

    show(index, announce) {
      const changed = this.current !== index;
      this.current = index;
      this.slides.forEach((slide, position) => {
        const active = position === index;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', this.dataset.slideType);
        slide.setAttribute('aria-label', slide.dataset.slideLabel);
      });
      this.counter.textContent = `${index + 1} / ${this.slides.length}`;
      if (announce && changed) this.status.textContent = this.slides[index].dataset.slideLabel;
      this.syncVideos();
    }

    go(index, animate = true, automatic = false) {
      index = (index + this.slides.length) % this.slides.length;
      this.autoMoving = automatic;
      this.show(index, !automatic);
      const left = this.slides[index].getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
      this.track.scrollTo({ left, behavior: animate && !this.motion.matches ? 'smooth' : 'instant' });
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.toggle) {
        this.toggle.hidden = this.motion.matches;
        this.toggle.setAttribute('aria-label', this.userPaused ? this.dataset.playLabel : this.dataset.pauseLabel);
        this.toggle.querySelector('[data-hero-toggle-icon]').textContent = this.userPaused ? '\u25b6' : '\u275a\u275a';
      }
      this.status.setAttribute('aria-live', this.autoplay && !this.userPaused && !this.motion.matches ? 'off' : 'polite');
      if (!this.autoplay || this.userPaused || this.hovered || this.focusPaused || !this.visible || document.hidden || this.motion.matches || window.Shopify?.designMode) return;
      this.timer = setTimeout(() => this.go(this.current + 1, true, true), this.interval);
    }

    syncVideos() {
      this.slides.forEach((slide, index) => slide.querySelectorAll('video').forEach(video => {
        if (index !== this.current || !this.visible || document.hidden || this.motion.matches) video.pause();
        else if (video.autoplay) video.play().catch(() => {});
      }));
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.scrollTimer);
      this.abort?.abort();
      this.abort = null;
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  }

  customElements.define('lemurea-hero-carousel', LemureaHeroCarousel);
})();
